/**
 * approach-moments.ts — pure approach/arrival state logic for the Experimental
 * Premium Live Map Layer (PLA-262 / PLA-267).
 *
 * CONTRACT
 * --------
 * This module is intentionally pure TypeScript: ZERO imports from the DOM,
 * google.maps, or React. The map renderer consumes the derived state; nothing
 * here touches a platform API, so it is unit-testable in plain jest.
 *
 * State model
 *   "far" -> "approaching" -> "imminent" -> "arrived"   (per tracking leg)
 *
 * Promotion (vehicle getting CLOSER) happens as soon as the distance crosses
 * an ENTER threshold (defaults: approaching <= 2000 m, imminent <= 500 m,
 * arrived <= 60 m). Multi-level jumps are allowed (e.g. a first reading of
 * 40 m goes straight from "far" to "arrived").
 *
 * Demotion (distance growing again) is HYSTERESIS-guarded: leaving a level
 * requires exceeding that level's enter threshold by `hysteresisRatio`
 * (default 25%). Example: "approaching" -> "far" only above 2500 m,
 * "imminent" -> "approaching" only above 625 m. GPS scatter around a
 * threshold therefore never flaps the state.
 *
 * "arrived" LATCHES for the current leg: once arrived, no distance reading
 * can un-arrive the state. Only a leg change (any transition between the three
 * legs — pickup, toHub, return) resets the machine to "far" — and that reset
 * emits `roleSwap: true` on exactly that one derived state, so the renderer can
 * play its destination role-swap moment once.
 *
 * Hold semantics: when `isLiveDriving` is false, or the distance reading is
 * not a finite number, the previous state is held (no transitions). A leg
 * change is still honoured while holding.
 *
 * Renderer helpers:
 *   - pulseIntensity(state)        -> 0 | 0.33 | 0.66 | 1 (drives the halo pulse)
 *   - dockingTriggered(prev, next) -> true exactly on the transition INTO
 *     "arrived" (suppressed entirely when `reducedMotion` is configured)
 */

/** Approach level for the active tracking leg, ordered far -> arrived. */
export type ApproachLevel = "far" | "approaching" | "imminent" | "arrived"

/**
 * Which leg of the round-trip the live vehicle is currently driving.
 *  - "pickup": hub -> customer, empty, to collect the car (destination = customer)
 *  - "toHub":  customer -> hub, driving the customer's car for inspection
 *              (destination = hub) — phase 3 with isReturn set
 *  - "return": hub -> customer, delivering the inspected car (destination = customer)
 */
export type TrackingLeg = "pickup" | "toHub" | "return"

/** Tunable thresholds + flags. All distances in meters. */
export interface ApproachMomentsConfig {
  /** Enter "approaching" at or below this distance. Default 2000. */
  approachingEnterMeters: number
  /** Enter "imminent" at or below this distance. Default 500. */
  imminentEnterMeters: number
  /** Enter "arrived" at or below this distance. Default 60. */
  arrivedEnterMeters: number
  /**
   * Demoting out of a level requires exceeding its enter threshold by this
   * ratio (0.25 = +25%), so GPS scatter never flaps the state. Default 0.25.
   */
  hysteresisRatio: number
  /** When true, dockingTriggered() always returns false. Default false. */
  reducedMotion: boolean
}

export const DEFAULT_APPROACH_CONFIG: ApproachMomentsConfig = {
  approachingEnterMeters: 2000,
  imminentEnterMeters: 500,
  arrivedEnterMeters: 60,
  hysteresisRatio: 0.25,
  reducedMotion: false,
}

/** Immutable snapshot of the approach state machine. */
export interface ApproachState {
  level: ApproachLevel
  /** Leg this state belongs to; a differing input leg resets the machine. */
  leg: TrackingLeg
  /**
   * True ONLY on the single state emitted by the deriving call in which the
   * leg changed (pickup -> return). Every other derived state carries false.
   */
  roleSwap: boolean
}

/** Per-tick input the tracking layer feeds into deriveApproachState. */
export interface ApproachInput {
  /** Straight-line or routed distance from vehicle to leg destination. */
  distanceToDestinationMeters: number
  /** False while no live driving session is active — state holds. */
  isLiveDriving: boolean
  /** Current leg ("pickup" | "toHub" | "return"); changing it resets to "far". */
  leg: TrackingLeg
}

const LEVEL_ORDER: Record<ApproachLevel, number> = {
  far: 0,
  approaching: 1,
  imminent: 2,
  arrived: 3,
}

const LEVELS_BY_ORDER: ApproachLevel[] = ["far", "approaching", "imminent", "arrived"]

function resolveConfig(config?: Partial<ApproachMomentsConfig>): ApproachMomentsConfig {
  return { ...DEFAULT_APPROACH_CONFIG, ...config }
}

/** Enter threshold (meters) for a promotable level; far has none. */
function enterThresholdMeters(level: ApproachLevel, cfg: ApproachMomentsConfig): number {
  switch (level) {
    case "arrived":
      return cfg.arrivedEnterMeters
    case "imminent":
      return cfg.imminentEnterMeters
    case "approaching":
      return cfg.approachingEnterMeters
    case "far":
      return Number.POSITIVE_INFINITY
  }
}

/** Highest level whose enter threshold the distance satisfies. */
function levelForDistance(distanceMeters: number, cfg: ApproachMomentsConfig): ApproachLevel {
  if (distanceMeters <= cfg.arrivedEnterMeters) return "arrived"
  if (distanceMeters <= cfg.imminentEnterMeters) return "imminent"
  if (distanceMeters <= cfg.approachingEnterMeters) return "approaching"
  return "far"
}

/**
 * Initial machine state for a leg: "far", no roleSwap pending.
 * Pass this (or null) as `prev` on the first derivation.
 */
export function createInitialApproachState(leg: TrackingLeg = "pickup"): ApproachState {
  return { level: "far", leg, roleSwap: false }
}

/**
 * Derive the next approach state from the previous state and a fresh input.
 *
 * Pure function: never mutates `prev`; always returns a new object.
 *
 * Rules (in order):
 *  1. `prev` null/undefined -> fresh "far" machine for `input.leg`, then the
 *     distance is applied through normal enter thresholds (no roleSwap).
 *  2. Leg change (any of pickup/toHub/return -> a different one) -> reset to
 *     "far", clear the arrived latch, set `roleSwap: true` for this one state,
 *     then apply the distance through normal enter thresholds.
 *  3. Not live driving, or non-finite distance -> hold previous level.
 *  4. "arrived" is latched for the leg — never demoted by distance.
 *  5. Promotion uses enter thresholds; demotion steps down one level at a
 *     time and each step requires distance > enterThreshold * (1 + hysteresisRatio).
 */
export function deriveApproachState(
  prev: ApproachState | null | undefined,
  input: ApproachInput,
  config?: Partial<ApproachMomentsConfig>,
): ApproachState {
  const cfg = resolveConfig(config)

  const legChanged = prev != null && prev.leg !== input.leg
  const baseline: ApproachState =
    prev == null || legChanged ? createInitialApproachState(input.leg) : prev
  const roleSwap = legChanged

  const distance = input.distanceToDestinationMeters
  const distanceUsable = input.isLiveDriving && Number.isFinite(distance)

  if (!distanceUsable) {
    return { level: baseline.level, leg: input.leg, roleSwap }
  }

  // Arrived latches for the current leg (baseline is already reset on leg change).
  if (baseline.level === "arrived") {
    return { level: "arrived", leg: input.leg, roleSwap }
  }

  const clamped = Math.max(0, distance)
  const target = levelForDistance(clamped, cfg)
  let nextOrder = LEVEL_ORDER[baseline.level]
  const targetOrder = LEVEL_ORDER[target]

  if (targetOrder > nextOrder) {
    // Promotion: enter thresholds apply immediately, multi-level jumps allowed.
    nextOrder = targetOrder
  } else {
    // Demotion: one level at a time, each gated by the +hysteresis exit bound.
    while (nextOrder > targetOrder) {
      const currentLevel = LEVELS_BY_ORDER[nextOrder]
      const exitBound = enterThresholdMeters(currentLevel, cfg) * (1 + cfg.hysteresisRatio)
      if (clamped <= exitBound) break
      nextOrder -= 1
    }
  }

  return { level: LEVELS_BY_ORDER[nextOrder], leg: input.leg, roleSwap }
}

/**
 * Halo pulse intensity for the renderer, monotonic in urgency:
 * far 0, approaching 0.33, imminent 0.66, arrived 1.
 */
export function pulseIntensity(state: ApproachState): 0 | 0.33 | 0.66 | 1 {
  switch (state.level) {
    case "far":
      return 0
    case "approaching":
      return 0.33
    case "imminent":
      return 0.66
    case "arrived":
      return 1
  }
}

/**
 * True exactly when this derivation crossed INTO "arrived" (the renderer's
 * one-shot docking animation cue). Always false when `reducedMotion` is set
 * in config. `prev` null/undefined counts as not-arrived, so a first-ever
 * reading inside the arrived radius still triggers docking once.
 */
export function dockingTriggered(
  prev: ApproachState | null | undefined,
  next: ApproachState,
  config?: Partial<ApproachMomentsConfig>,
): boolean {
  const cfg = resolveConfig(config)
  if (cfg.reducedMotion) return false
  return next.level === "arrived" && prev?.level !== "arrived"
}
